/**
 * Modal-Grundgerüst.
 *
 * Kopfzeile auf gedecktem Grund, weißer Körper, Schatten wie im Design.
 * Escape schließt, ein Klick auf den Hintergrund ebenfalls – außer das Modal
 * ist als „fest" markiert (etwa während eines laufenden Speichervorgangs).
 */

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  titel: string;
  untertitel?: ReactNode;
  breite?: number;
  /** Abstand oben – im Design je nach Modal 54 oder 70 Pixel. */
  abstandOben?: number;
  fest?: boolean;
  zeigeSchliessen?: boolean;
  onSchliessen: () => void;
  kopfZusatz?: ReactNode;
  fuss?: ReactNode;
  children: ReactNode;
}

export function Modal({
  titel,
  untertitel,
  breite = 520,
  abstandOben = 54,
  fest = false,
  zeigeSchliessen = false,
  onSchliessen,
  kopfZusatz,
  fuss,
  children,
}: ModalProps) {
  useEffect(() => {
    function taste(e: KeyboardEvent) {
      if (e.key === 'Escape' && !fest) onSchliessen();
    }
    document.addEventListener('keydown', taste);
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', taste);
      document.body.style.overflow = vorher;
    };
  }, [fest, onSchliessen]);

  return createPortal(
    <div
      className="modal-scrim"
      style={{ padding: `${abstandOben}px 24px` }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !fest) onSchliessen();
      }}
    >
      <div className="modal-karte" style={{ maxWidth: breite }} role="dialog" aria-modal="true">
        <div className="modal-kopf">
          <div>
            <div className="modal-titel">{titel}</div>
            {untertitel && <div className="modal-untertitel">{untertitel}</div>}
          </div>
          {kopfZusatz}
          {zeigeSchliessen && (
            <button type="button" className="modal-x" title="Schließen" onClick={onSchliessen}>
              ✕
            </button>
          )}
        </div>

        <div className="modal-koerper">{children}</div>

        {fuss && <div className="modal-fuss">{fuss}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Sekundärer Knopf im Modal-Fuß. */
export function KnopfSekundaer({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" className="modal-knopf-sek" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

/** Primärer Knopf im Modal-Fuß; „ton" wählt Rot oder Grün. */
export function KnopfPrimaer({
  children,
  onClick,
  ton = 'rot',
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  ton?: 'rot' | 'gruen';
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`modal-knopf-prim ${ton}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
